import { useLayoutEffect, useRef, useState } from 'react';
export type HasOverflow = { height: number; hasOverflow: boolean };

// This hook is used to determine if an element has overflow. It returns a boolean flag,
// and the scroll height of the element. In the case of overflow, the scroll height will
// be greater than the client height, but the scroll height can be used to set CSS max-height
// (for example) as a base for animations when the element is expanded.
export const useHasOverflow = (): [HasOverflow, React.RefObject<HTMLDivElement>] => {
	const [hasOverflow, setHasOverflow] = useState<HasOverflow>({ height: 0, hasOverflow: false });
	const ref = useRef<HTMLDivElement>(null);

	useLayoutEffect(() => {
		const trigger = () => {
			const hasOverflow = Math.abs((ref?.current?.scrollHeight || 0) - (ref?.current?.clientHeight || 0)) > 2;
			setHasOverflow({ hasOverflow, height: ref?.current?.scrollHeight || 0 });
		};
		let observer: ResizeObserver;
		if (ref.current) {
			if ('ResizeObserver' in window) {
				observer = new ResizeObserver(trigger);
				observer.observe(ref.current);
			}

			trigger();
		}
		return () => {
			if (observer) {
				observer.disconnect();
			}
		};
	}, []);

	return [hasOverflow, ref];
};
